import {
  decodeGooglePolyline,
  routeBounds,
  routeLineString,
  type ComputedRoutePlan,
  type RouteRoutingProfile,
  type RouteWaypoint,
} from '@sixam/shared/route-planner-core';

const DEFAULT_VALHALLA_URL = 'https://valhalla1.openstreetmap.de/route';

type ValhallaResponse = {
  error?: string;
  trip?: {
    legs?: { shape?: string }[];
    summary?: { length?: number; time?: number };
  };
};

export async function computeValhallaRoute(
  waypoints: readonly RouteWaypoint[],
  routingProfile: RouteRoutingProfile,
  signal?: AbortSignal
): Promise<ComputedRoutePlan> {
  const response = await fetch(process.env.EXPO_PUBLIC_VALHALLA_URL ?? DEFAULT_VALHALLA_URL, {
    body: JSON.stringify({
      costing: routingProfile === 'foot-walking' ? 'pedestrian' : 'bicycle',
      directions_type: 'none',
      locations: waypoints.map(({ latitude: lat, longitude: lon }) => ({ lat, lon })),
      units: 'kilometers',
    }),
    headers: { 'Content-Type': 'application/json' },
    method: 'POST',
    signal,
  });
  const body = (await response.json().catch(() => null)) as ValhallaResponse | null;
  if (!response.ok || !body?.trip) {
    throw new Error(body?.error ?? `Fallback route calculation failed (${response.status}).`);
  }

  const coordinates = (body.trip.legs ?? []).flatMap((leg, index) => {
    const legCoordinates = leg.shape ? decodeGooglePolyline(leg.shape, 6) : [];
    return index === 0 ? legCoordinates : legCoordinates.slice(1);
  });
  const distanceM = (body.trip.summary?.length ?? 0) * 1000;
  if (coordinates.length < 2 || distanceM <= 0) {
    throw new Error('No runnable path was found between those waypoints.');
  }

  return {
    bounds: routeBounds(coordinates),
    distanceM,
    durationSeconds: body.trip.summary?.time ?? null,
    elevationGainM: null,
    geometry: routeLineString(coordinates),
    provider: 'valhalla',
    routingProfile,
    waypoints,
  };
}
